/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { HttpBackend, HttpRequest, HttpResponse } from '@angular/common/http';
import { SourceMap } from '@angular/compiler';
import { Injectable, inject } from '@angular/core';

import { Observable, catchError, filter, map, of, shareReplay } from 'rxjs';
import * as vlq from 'vlq';

import { LogPosition } from './log-position';

@Injectable({
  providedIn: 'root'
})
export class MapperService {
  private httpBackend = inject(HttpBackend);

  /** cache for source maps, key is source map location, ie. 'http://localhost:4200/main.js.map' */
  private sourceMapCache: Map<string, Observable<SourceMap | null>> = new Map();

  /** cache for specific log position, key is the dist position, ie 'main.js:339:21' */
  private logPositionCache: Map<string, Observable<LogPosition>> = new Map();

  /**
   * @returns The log position of the caller using source maps if available
   */
  public getLogPosition(error?: Error): Observable<LogPosition> {
    const stackLine = this.getStackLine(error);

    // if we were not able to parse the stackLine, just return an empty Log Position
    if (!stackLine) {
      return of({ fileName: '', lineNumber: 0, columnNumber: 0 });
    }

    const logPosition = this.getLocalPosition(stackLine);

    if (logPosition.fileName === 'unknown') {
      // if the filename is unknown, don't try to use source maps
      return of(logPosition);
    }

    const sourceMapLocation = this.getSourceMapLocation(stackLine);
    return this.getSourceMap(sourceMapLocation, logPosition);
  }

  /**
   * Get the stackline of the original caller, or the first stackline of the given error
   */
  private getStackLine(error?: Error): string | null {
    let defaultProxy = 0;

    if (!error) {
      // If no error was passed in, create one and go back 4 steps in the stack to get the caller of the log.
      defaultProxy = 4;
      error = new Error();
    }

    try {
      // Here are different examples of stacktrace

      // Firefox (last line is the user code, the 4 first are ours):
      // getStackLine@http://localhost:4200/main.js:358:23
      // getLogPosition@http://localhost:4200/main.js:557:44
      // _log@http://localhost:4200/main.js:830:28
      // error@http://localhost:4200/main.js:652:14
      // handleLog@http://localhost:4200/main.js:1158:29

      // Chrome and Edge (last line is the user code):
      // Error
      // at MapperService.getStackLine (mapper.service.ts:48:15)
      // at MapperService.getLogPosition (mapper.service.ts:26:28)
      // at LoggerService._log (logger.service.ts:55:8)
      // at LoggerService.error (logger.service.ts:27:10)
      // at AppComponent.handleLog (app.component.ts:38:8)

      const firstStackLine = error.stack?.split('\n')[0];
      if (firstStackLine === undefined || !firstStackLine.includes('.js:')) {
        // The stacktrace starts with no function call (example in Chrome or Edge)
        defaultProxy = defaultProxy + 1;
      }

      return error.stack?.split('\n')[defaultProxy] || null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Get LogPosition from the given stack line
   */
  private getLocalPosition(stackLine: string): LogPosition {
    // strip base path, then parse filename, line, and column, stackline looks like this :
    // Firefox
    // handleLog@http://localhost:4200/main.js:1158:29
    // Chrome and Edge
    // at AppComponent.handleLog (app.component.ts:38)

    const positionStartIndex = stackLine.lastIndexOf('/');
    let positionEndIndex: number | undefined = stackLine.indexOf(')');
    if (positionEndIndex < 0) {
      positionEndIndex = undefined;
    }

    const position = stackLine.substring(positionStartIndex + 1, positionEndIndex);
    const dataArray = position.split(':');
    if (dataArray.length === 3) {
      return { fileName: dataArray[0], lineNumber: +dataArray[1], columnNumber: +dataArray[2] };
    }
    return { fileName: 'unknown', lineNumber: 0, columnNumber: 0 };
  }

  private getTranspileLocation(stackLine: string): string {
    // Example stackLine:
    // Firefox : getStackLine@http://localhost:4200/main.js:358:23
    // Chrome and Edge : at MapperService.getStackLine (mapper.service.ts:48:15)
    let locationStartIndex = stackLine.indexOf('(');
    if (locationStartIndex < 0) {
      locationStartIndex = stackLine.lastIndexOf('@');
      if (locationStartIndex < 0) {
        locationStartIndex = stackLine.lastIndexOf(' ');
      }
    }

    let locationEndIndex: number | undefined = stackLine.indexOf(')');
    if (locationEndIndex < 0) {
      locationEndIndex = undefined;
    }

    return stackLine.substring(locationStartIndex + 1, locationEndIndex);
  }

  /**
   * Gets the URL of the sourcemap (the URL can be relative or absolute, it is browser dependant)
   */
  private getSourceMapLocation(stackLine: string): string {
    const file = this.getTranspileLocation(stackLine);
    const mapFullPath = file.substring(0, file.lastIndexOf(':'));
    return mapFullPath.substring(0, mapFullPath.lastIndexOf(':')) + '.map';
  }

  private getMapping(sourceMap: SourceMap, position: LogPosition): LogPosition {
    const zeroBasedLineNumber = (position.lineNumber || 1) - 1;
    const zeroBasedColumnNumber = (position.columnNumber || 1) - 1;

    let previousSegment: number[] = [0, 0, 0, 0];

    // => ';' indicates end of a line
    // => ',' separates mappings in a line
    // decoded mapping => [ generatedCodeColumn, sourceFileIndex, sourceCodeLine, sourceCodeColumn, nameIndex ]
    let sourceFileIndex = 0, // second field
      sourceCodeLine = 0, // third field
      sourceCodeColumn = 0; // fourth field

    const lines = sourceMap.mappings.split(';');

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      // reset column position to 0 after each line
      let generatedCodeColumn = 0;
      // decode sections in line
      const columns = lines[lineIndex].split(',');

      for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
        const decodedSection = vlq.decode(columns[columnIndex]);
        generatedCodeColumn += decodedSection[0];

        if (decodedSection.length >= 4) {
          // update relative positions
          sourceFileIndex += decodedSection[1];
          sourceCodeLine += decodedSection[2];
          sourceCodeColumn += decodedSection[3];
        }

        // check if matching map
        if (lineIndex === zeroBasedLineNumber) {
          if (generatedCodeColumn === zeroBasedColumnNumber) {
            // matching column and line found
            return {
              fileName: sourceMap.sources[sourceFileIndex],
              lineNumber: sourceCodeLine + 1,
              columnNumber: sourceCodeColumn + 1
            };
          } else if (previousSegment[0] < zeroBasedColumnNumber && zeroBasedColumnNumber < generatedCodeColumn) {
            return {
              fileName: sourceMap.sources[previousSegment[1]],
              lineNumber: previousSegment[2] + 1,
              columnNumber: previousSegment[3] + 1
            };
          } else if (columnIndex + 1 === columns.length) {
            // matching column not found, but line is correct
            return { fileName: sourceMap.sources[sourceFileIndex], lineNumber: sourceCodeLine + 1, columnNumber: 0 };
          }
        }
        previousSegment = [generatedCodeColumn, sourceFileIndex, sourceCodeLine, sourceCodeColumn];
      }
    }
    // Parsing source maps failed, just return original LogPosition, it's still better than nothing.
    return position;
  }

  private getSourceMap(sourceMapLocation: string, distPosition: LogPosition): Observable<LogPosition> {
    const req = new HttpRequest<SourceMap>('GET', sourceMapLocation);
    const distPositionKey = `${distPosition.fileName}:${distPosition.lineNumber}:${distPosition.columnNumber}`;

    // if the specific log position is already in cache return it
    if (this.logPositionCache.has(distPositionKey)) {
      return this.logPositionCache.get(distPositionKey)!;
    }

    // otherwise check if the source map is already cached for given source map location
    if (!this.sourceMapCache.has(sourceMapLocation)) {
      // obtain the source map if not cached
      this.sourceMapCache.set(
        sourceMapLocation,
        this.httpBackend.handle(req).pipe(
          filter((event) => event instanceof HttpResponse),
          map((resp) => (resp as HttpResponse<SourceMap>).body),
          shareReplay(1)
        )
      );
    }

    // at this point the source map is cached, use it to get specific log position mapping
    const logPosition$ = this.sourceMapCache.get(sourceMapLocation)!.pipe(
      map((sourceMap) => {
        // sourceMap can be null if HttpBackend is not provided for example
        if (!sourceMap) {
          return distPosition;
        }
        // map generated position to source position
        return this.getMapping(sourceMap, distPosition);
      }),
      catchError((e) => {
        console.log(e);
        return of(distPosition);
      }),
      shareReplay(1)
    );

    // store specific log position in cache for given dest position and return it
    this.logPositionCache.set(distPositionKey, logPosition$);

    return logPosition$;
  }
}
